import React, { type ReactNode, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";

export type CharacterConversationLayout = "full" | "condensed" | "reveal";

interface CharacterConversationContainerProps {
  layout?: CharacterConversationLayout;
  characterImageSrc: string;
  characterImageAlt?: string;
  /** Custom ReactNode to render instead of the default <img>. When provided, characterImageSrc is ignored for rendering (still used as fallback). */
  characterImageNode?: ReactNode;
  /** When true, the character image renders behind the speech bubble (z-index below). */
  characterImageBehind?: boolean;
  messageTitle?: ReactNode;
  message: ReactNode;
  actionContent: ReactNode;
  ctaContent?: ReactNode;
  noActionCardWrapper?: boolean;
  scrollToActionKey?: string | number;
  onRevealComplete?: () => void;
}

type LayoutConfig = {
  wrapper: string;
  imageColumn: string;
  imageInner: string;
  imageClass: string;
  conversationColumn: string;
  conversationInner: string;
  bubble: string;
  connectorWrapper: string;
  connectorPill: string;
  connectorDot: string;
  actionCard: string;
  ctaWrapper: string;
};

const LAYOUT_TRANSITION = {
  type: "spring" as const,
  stiffness: 260,
  damping: 28,
  mass: 0.6,
};

const ACTION_CONTENT_FADE = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.2, delay: 0.2 },
};

const LAYOUT_MAP: Record<"full" | "condensed", LayoutConfig> = {
  full: {
    wrapper: "pt-6 flex flex-col items-center gap-0",
    imageColumn: "flex justify-center flex-shrink-0 z-1",
    imageInner: "relative",
    imageClass: "relative h-[160px]",
    conversationColumn:
      "w-full flex flex-col gap-0 overflow-visible items-center",
    conversationInner:
      "relative w-full max-w-sm overflow-visible flex flex-col gap-0",
    bubble:
      "relative bg-white/95 backdrop-blur-xl border-white/60 px-6 py-5 space-y-3 rounded-t-3xl rounded-b-xl overflow-visible",
    connectorWrapper: "relative h-8 flex items-center justify-left",
    connectorPill:
      "w-30 h-8 bg-white rounded-full flex items-center justify-between",
    connectorDot: "w-8 h-8 bg-gray-100 rounded-full",
    actionCard: "relative bg-white overflow-visible rounded-b-3xl rounded-t-xl",
    ctaWrapper: "mt-4",
  },
  condensed: {
    wrapper: "pt-6 flex flex-row items-start gap-4",
    imageColumn: "flex justify-center items-center basis-1/3 flex-shrink-0",
    imageInner: "relative",
    imageClass:
      "relative h-[160px] drop-shadow-[0_18px_40px_rgba(30,64,175,0.35)]",
    conversationColumn: "basis-2/3 flex flex-col overflow-visible",
    conversationInner: "relative w-full overflow-visible",
    bubble:
      "relative bg-white/95 backdrop-blur-xl border-white/60 px-6 py-4 space-y-3 rounded-t-3xl rounded-b-xl overflow-visible",
    connectorWrapper: "relative h-8 flex items-center justify-left",
    connectorPill:
      "w-28 h-8 bg-white rounded-full flex items-center justify-between",
    connectorDot: "w-8 h-8 bg-gray-100 rounded-full",
    actionCard: "relative bg-white overflow-visible rounded-b-3xl rounded-t-xl",
    ctaWrapper: "mt-4",
  },
};

export function CharacterConversationContainer({
  layout = "full",
  characterImageSrc,
  characterImageAlt = "Character",
  characterImageNode,
  characterImageBehind = false,
  messageTitle,
  message,
  actionContent,
  ctaContent,
  noActionCardWrapper = false,
  scrollToActionKey,
  onRevealComplete: _onRevealComplete,
}: CharacterConversationContainerProps) {
  const config = layout === "reveal" ? LAYOUT_MAP.full : LAYOUT_MAP[layout];
  const actionCardRef = useRef<HTMLDivElement>(null);

  // Scroll the action card into view when scrollToActionKey changes
  useEffect(() => {
    if (scrollToActionKey !== undefined && actionCardRef.current) {
      // Small delay to allow the DOM to update before scrolling
      const timeoutId = setTimeout(() => {
        actionCardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [scrollToActionKey]);

  const showTitle = messageTitle !== null;
  const resolvedTitle = showTitle ? messageTitle ?? "Emberlee" : null;
  const titleNode =
    typeof resolvedTitle === "string" ? (
      <span className="text-xs font-semibold uppercase tracking-[0.22em]">
        {resolvedTitle}
      </span>
    ) : (
      resolvedTitle
    );

  const messageNode =
    typeof message === "string" ? (
      <div className="text-sm text-gray-700 leading-relaxed [&_strong]:font-bold [&_em]:italic [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_p+p]:mt-3">
        <ReactMarkdown>{message}</ReactMarkdown>
      </div>
    ) : (
      <div className="text-sm text-gray-700 leading-relaxed">{message}</div>
    );

  // Reveal layout - character peeks up, pops onto speech bubble, message types in
  if (layout === "reveal") {
    return (
      <motion.div
        layout
        className="flex flex-col items-center gap-0 pt-12"
        transition={LAYOUT_TRANSITION}
      >
        {/* Character image - overlaps the top of the speech bubble */}
        <motion.div
          layout
          className="relative z-20 mb-[-40px]"
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 20,
            mass: 0.8,
          }}
        >
          {characterImageNode ? (
              <div className="w-[160px] h-[160px] overflow-visible flex items-end justify-center">
              {characterImageNode}
            </div>
          ) : (
            <img
              src={characterImageSrc}
              alt={characterImageAlt}
              className="w-[160px] h-[160px] object-contain"
            />
          )}
        </motion.div>

        {/* Speech bubble */}
        <AnimatePresence>
          {message && (
            <motion.div
              className="relative z-10 w-full max-w-xs"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              <div className="bg-white/95 backdrop-blur-xl rounded-3xl px-6 py-5 space-y-3 shadow-lg">
                {showTitle && (
                  <div className="flex items-center gap-2 text-gray-500/80">
                    <span className="inline-flex h-2 w-2 rounded-full bg-[#FFB100] animate-pulse" />
                    {titleNode}
                  </div>
                )}
                {messageNode}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action content slides in below after reveal settles */}
        <AnimatePresence>
          {actionContent && (
            <motion.div
              className="w-full max-w-xs"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="bg-white overflow-visible rounded-3xl">
                {actionContent}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  // Condensed layout needs special structure (image + bubble row, then full-width below)
  if (layout === "condensed") {
    return (
      <motion.div
        layout
        // max-w-lg matches FlowFooter's inner column so the action card and
        // the footer button read as one column on wide (desktop) screens.
        className="mx-auto w-full max-w-lg flex flex-col gap-0"
        transition={LAYOUT_TRANSITION}
      >
        {/* Image centered above speech bubble (smaller than full layout) */}
        <motion.div
          layout
          layoutId="character-image-col"
          className="flex justify-center"
          style={{ zIndex: characterImageBehind ? 0 : 2 }}
          transition={LAYOUT_TRANSITION}
        >
          <motion.div layout className="relative" transition={LAYOUT_TRANSITION}>
            {characterImageNode ? (
              <motion.div
                layout
                className="relative h-[160px] overflow-visible"
                transition={LAYOUT_TRANSITION}
              >
                {characterImageNode}
              </motion.div>
            ) : (
              <motion.img
                layout
                src={characterImageSrc}
                alt={characterImageAlt}
                className="relative h-[160px] object-contain"
                transition={LAYOUT_TRANSITION}
              />
            )}
          </motion.div>
        </motion.div>

        {/* Speech bubble below image */}
        <motion.div
          layout
          layoutId="speech-bubble"
          className="w-full overflow-visible"
          style={{ zIndex: characterImageBehind ? 1 : 0 }}
          transition={LAYOUT_TRANSITION}
        >
          <motion.div
            layout
            className="relative bg-white/95 backdrop-blur-xl border-white/60 px-6 py-4 space-y-3 rounded-t-3xl rounded-b-xl overflow-visible"
            transition={LAYOUT_TRANSITION}
          >
            {showTitle && (
              <div className="flex items-center gap-2 text-gray-500/80">
                <span className="inline-flex h-2 w-2 rounded-full bg-[#FFB100] animate-pulse" />
                {titleNode}
              </div>
            )}
            {messageNode}
          </motion.div>
        </motion.div>

        {/* Connector and Action Card - only when actionContent is provided */}
        {actionContent && (
          <>
            <motion.div
              layout
              layoutId="connector-wrapper"
              className="relative h-8 flex items-center justify-end"
              transition={LAYOUT_TRANSITION}
            >
              <motion.div
                layout
                className="w-28 h-8 bg-white/90 rounded-full flex items-center justify-between"
                transition={LAYOUT_TRANSITION}
              >
                <div className="w-8 h-8 bg-gray-100 rounded-full" />
                <div className="w-8 h-8 bg-gray-100 rounded-full" />
              </motion.div>
            </motion.div>

            {noActionCardWrapper ? (
              <motion.div
                ref={actionCardRef}
                layoutId="action-card"
                transition={LAYOUT_TRANSITION}
              >
                <motion.div key={scrollToActionKey} {...ACTION_CONTENT_FADE}>
                  {actionContent}
                </motion.div>
              </motion.div>
            ) : (
              <motion.div
                ref={actionCardRef}
                layoutId="action-card"
                className="relative bg-white overflow-visible rounded-b-3xl rounded-t-xl"
                transition={LAYOUT_TRANSITION}
              >
                <motion.div key={scrollToActionKey} {...ACTION_CONTENT_FADE}>
                  {actionContent}
                </motion.div>
              </motion.div>
            )}
          </>
        )}

        {/* CTA - full width */}
        {ctaContent ? (
          <motion.div layout className="mt-4" transition={LAYOUT_TRANSITION}>
            {ctaContent}
          </motion.div>
        ) : null}
      </motion.div>
    );
  }

  // Full layout (character stacked above speech bubble)
  return (
    <motion.div
      layout
      className=" flex flex-col gap-0"
      transition={LAYOUT_TRANSITION}
    >
      <motion.div
        layout
        className={config.wrapper}
        transition={LAYOUT_TRANSITION}
      >
        <motion.div
          layout
          layoutId="character-image-col"
          className={config.imageColumn}
          transition={LAYOUT_TRANSITION}
        >
          <motion.div
            layout
            className={config.imageInner}
            transition={LAYOUT_TRANSITION}
          >
            <motion.div
              layout
              className="absolute inset-0 -translate-y-4 rounded-full   blur-3xl"
              transition={LAYOUT_TRANSITION}
            />
            {characterImageNode ? (
              <motion.div
                layout
                className={`${config.imageClass} overflow-visible`}
                transition={LAYOUT_TRANSITION}
              >
                {characterImageNode}
              </motion.div>
            ) : (
              <motion.img
                layout
                src={characterImageSrc}
                alt={characterImageAlt}
                className={config.imageClass}
                transition={LAYOUT_TRANSITION}
              />
            )}
          </motion.div>
        </motion.div>

        <motion.div
          layout
          layoutId="speech-bubble"
          className={config.conversationColumn}
          transition={LAYOUT_TRANSITION}
        >
          <motion.div
            layout
            className={config.conversationInner}
            transition={LAYOUT_TRANSITION}
          >
            <motion.div
              layout
              className={config.bubble}
              transition={LAYOUT_TRANSITION}
            >
              {showTitle && (
                <div className="flex items-center gap-2 text-gray-500/80">
                  <span className="inline-flex h-2 w-2 rounded-full bg-[#FFB100] animate-pulse" />
                  {titleNode}
                </div>
              )}
              {messageNode}
            </motion.div>

            {actionContent && (
              <>
                <motion.div
                  layout
                  layoutId="connector-wrapper"
                  className={config.connectorWrapper}
                  transition={LAYOUT_TRANSITION}
                >
                  <motion.div
                    layout
                    className={config.connectorPill}
                    transition={LAYOUT_TRANSITION}
                  >
                    <div className={config.connectorDot} />
                    <div className={config.connectorDot} />
                  </motion.div>
                </motion.div>

                {noActionCardWrapper ? (
                  <motion.div
                    ref={actionCardRef}
                    layoutId="action-card"
                    transition={LAYOUT_TRANSITION}
                  >
                    <motion.div key={scrollToActionKey} {...ACTION_CONTENT_FADE}>
                      {actionContent}
                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div
                    ref={actionCardRef}
                    layoutId="action-card"
                    className={config.actionCard}
                    transition={LAYOUT_TRANSITION}
                  >
                    <motion.div key={scrollToActionKey} {...ACTION_CONTENT_FADE}>
                      {actionContent}
                    </motion.div>
                  </motion.div>
                )}
              </>
            )}

            {ctaContent ? (
              <motion.div
                layout
                className={config.ctaWrapper}
                transition={LAYOUT_TRANSITION}
              >
                {ctaContent}
              </motion.div>
            ) : null}
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
